import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, prospectingAPI, servicePillarsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, LayoutGrid, LayoutList, BarChart3, Filter, Download, Building2, Users, Mail, AlertTriangle, X, Sparkles, Trash2, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import ClientModal from '../components/modals/ClientModal';
import AIDiscoverOrgsModal from '../components/modals/AIDiscoverOrgsModal';

var ORG_TYPES = ['GLC', 'Statutory Body', 'Private Sector', 'Government Agency', 'NGO', 'Other'];
var PRIORITIES = ['High', 'Medium', 'Low'];
var STATUSES = ['Identified', 'Researched', 'Contacted', 'In Discussion', 'Proposal Sent', 'Won', 'Lost', 'On Hold'];
var prioColors = { High: 'bg-red-100 text-red-700', Medium: 'bg-yellow-100 text-yellow-700', Low: 'bg-green-100 text-green-700' };
var statusColors = { 'Identified': 'bg-slate-100 text-slate-700', 'Researched': 'bg-blue-100 text-blue-700', 'Contacted': 'bg-indigo-100 text-indigo-700', 'In Discussion': 'bg-purple-100 text-purple-700', 'Proposal Sent': 'bg-amber-100 text-amber-700', 'Won': 'bg-green-100 text-green-700', 'Lost': 'bg-red-100 text-red-700', 'On Hold': 'bg-gray-100 text-gray-600' };

var Clients = function({ category }) {
  var navigate = useNavigate();
  var { isAdmin } = useAuth();
  var isProspects = category === 'prospect';
  var pageTitle = isProspects ? 'Prospects' : 'Clients';
  var basePath = isProspects ? '/prospects' : '/clients';

  var s1 = useState([]); var clients = s1[0]; var setClients = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(false); var showCreateModal = s3[0]; var setShowCreateModal = s3[1];
  var s4 = useState(null); var selectedClient = s4[0]; var setSelectedClient = s4[1];
  var s5 = useState(''); var searchTerm = s5[0]; var setSearchTerm = s5[1];
  var s6 = useState('card'); var viewMode = s6[0]; var setViewMode = s6[1];
  var s7 = useState(null); var stats = s7[0]; var setStats = s7[1];
  var s8 = useState(false); var showFilters = s8[0]; var setShowFilters = s8[1];
  var s9 = useState(false); var showAIDiscover = s9[0]; var setShowAIDiscover = s9[1];
  var s10 = useState(''); var filterOrgType = s10[0]; var setFilterOrgType = s10[1];
  var s11 = useState(''); var filterPriority = s11[0]; var setFilterPriority = s11[1];
  var s12 = useState(''); var filterStatus = s12[0]; var setFilterStatus = s12[1];
  var s13 = useState(''); var filterService = s13[0]; var setFilterService = s13[1];
  var s14 = useState([]); var servicePillars = s14[0]; var setServicePillars = s14[1];
  var s15 = useState('client_name'); var sortField = s15[0]; var setSortField = s15[1];
  var s16 = useState('asc'); var sortDir = s16[0]; var setSortDir = s16[1];
  var s17 = useState(null); var deleteTarget = s17[0]; var setDeleteTarget = s17[1];
  var s18 = useState(''); var adminPwd = s18[0]; var setAdminPwd = s18[1];
  var s19 = useState(null); var deleteError = s19[0]; var setDeleteError = s19[1];
  var s20 = useState(false); var deleting = s20[0]; var setDeleting = s20[1];

  var fetchClients = useCallback(async function() {
    try {
      var params = {};
      if (category) params.category = category;
      var r = await clientsAPI.getAll(params);
      setClients(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [category]);

  var fetchStats = useCallback(async function() {
    try { var r = await prospectingAPI.getStats(); setStats(r.data); } catch (e) {}
  }, []);

  var fetchPillars = useCallback(async function() {
    try { var r = await servicePillarsAPI.getAll(); setServicePillars(r.data); } catch (e) {}
  }, []);

  useEffect(function() {
    fetchClients();
    fetchStats();
    fetchPillars();
  }, [fetchClients, fetchStats, fetchPillars]);

  var handleEdit = function(c) { setSelectedClient(c); setShowCreateModal(true); };
  var handleModalClose = function() { setShowCreateModal(false); setSelectedClient(null); };
  var formatCurrency = function(v) { return 'RM ' + (v / 1000).toFixed(0) + 'K'; };

  var handleDelete = async function() {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError(null);
    try {
      var pwd = deleteTarget.has_deals ? adminPwd : null;
      await clientsAPI.delete(deleteTarget._id, pwd);
      setDeleteTarget(null); setAdminPwd('');
      fetchClients(); fetchStats();
    } catch (err) {
      var msg = 'Delete failed';
      if (err.response && err.response.data) msg = err.response.data.detail || msg;
      setDeleteError(msg);
    } finally { setDeleting(false); }
  };

  var handleSort = function(field) {
    if (sortField === field) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  var activeFilterCount = [filterOrgType, filterPriority, filterStatus, filterService].filter(Boolean).length;
  var clearFilters = function() { setFilterOrgType(''); setFilterPriority(''); setFilterStatus(''); setFilterService(''); };

  var filteredClients = useMemo(function() {
    var filtered = clients.filter(function(c) {
      var ms = !searchTerm || (c.client_name || '').toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 || (c.industry || '').toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 || (c.sector || '').toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
      var mt = !filterOrgType || c.organization_type === filterOrgType;
      var mp = !filterPriority || c.priority === filterPriority;
      var mst = !filterStatus || c.prospecting_status === filterStatus;
      var msv = !filterService || (c.relevant_services || []).indexOf(filterService) !== -1;
      return ms && mt && mp && mst && msv;
    });
    // Sort
    filtered.sort(function(a, b) {
      var va = a[sortField] || '';
      var vb = b[sortField] || '';
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [clients, searchTerm, filterOrgType, filterPriority, filterStatus, filterService, sortField, sortDir]);

  var exportCSV = function() {
    var headers = ['Name', 'Type', 'Industry', 'Sector', 'Priority', 'Status', 'Category', 'Website'];
    var rows = [];
    for (var i = 0; i < filteredClients.length; i++) {
      var c = filteredClients[i];
      rows.push([c.client_name, c.organization_type, c.industry, c.sector, c.priority, c.prospecting_status, c.category, c.website]);
    }
    var lines = [headers.join(',')];
    for (var r = 0; r < rows.length; r++) {
      var parts = [];
      for (var p = 0; p < rows[r].length; p++) parts.push('"' + (rows[r][p] || '') + '"');
      lines.push(parts.join(','));
    }
    var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a'); a.href = url; a.download = 'humancap_' + (category || 'all') + '.csv'; a.click();
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  // Pre-build filter options
  var orgOpts = [<option key="" value="">All Types</option>];
  for (var oi = 0; oi < ORG_TYPES.length; oi++) orgOpts.push(<option key={ORG_TYPES[oi]} value={ORG_TYPES[oi]}>{ORG_TYPES[oi]}</option>);
  var prioOpts = [<option key="" value="">All Priorities</option>];
  for (var pi = 0; pi < PRIORITIES.length; pi++) prioOpts.push(<option key={PRIORITIES[pi]} value={PRIORITIES[pi]}>{PRIORITIES[pi]}</option>);
  var statOpts = [<option key="" value="">All Statuses</option>];
  for (var si = 0; si < STATUSES.length; si++) statOpts.push(<option key={STATUSES[si]} value={STATUSES[si]}>{STATUSES[si]}</option>);
  var svcOpts = [<option key="" value="">All Services</option>];
  for (var svi = 0; svi < servicePillars.length; svi++) svcOpts.push(<option key={servicePillars[svi]._id} value={servicePillars[svi].pillar_name}>{servicePillars[svi].pillar_name}</option>);

  return (
    <div className="space-y-4" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-slate-500 mt-1 text-sm">{filteredClients.length} {isProspects ? 'prospects' : 'organizations'}{activeFilterCount > 0 ? ' (filtered from ' + clients.length + ')' : ''}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 text-sm" data-testid="export-csv-btn"><Download size={16} /> Export</button>
          <div className="flex border border-slate-200 rounded-lg overflow-hidden" data-testid="view-mode-toggle">
            <button onClick={function() { setViewMode('card'); }} className={'px-3 py-2 text-sm flex items-center gap-1.5 ' + (viewMode === 'card' ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50')} data-testid="card-view-btn"><LayoutGrid size={16} /> <span className="hidden sm:inline">Card</span></button>
            <button onClick={function() { setViewMode('list'); }} className={'px-3 py-2 text-sm flex items-center gap-1.5 ' + (viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50')} data-testid="list-view-btn"><LayoutList size={16} /> <span className="hidden sm:inline">List</span></button>
            <button onClick={function() { setViewMode('dashboard'); }} className={'px-3 py-2 text-sm flex items-center gap-1.5 ' + (viewMode === 'dashboard' ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50')} data-testid="dashboard-view-btn"><BarChart3 size={16} /> <span className="hidden sm:inline">Dashboard</span></button>
          </div>
          {isProspects && <button onClick={function() { setShowAIDiscover(true); }} className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 text-sm" data-testid="ai-discover-btn"><Sparkles size={18} /> AI Discover</button>}
          <button onClick={function() { setShowCreateModal(true); }} className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm" data-testid="new-client-btn"><Plus size={18} /> New {isProspects ? 'Prospect' : 'Client'}</button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input type="text" placeholder="Search by name, industry, sector..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" data-testid="search-input" />
          </div>
          <button onClick={function() { setShowFilters(!showFilters); }} className={'flex items-center gap-2 px-3 py-2 border rounded-lg text-sm ' + (activeFilterCount > 0 ? 'border-primary text-primary bg-primary/5' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid="toggle-filters-btn">
            <Filter size={16} /> Filters {activeFilterCount > 0 && <span className="w-5 h-5 bg-primary text-white rounded-full text-xs flex items-center justify-center">{activeFilterCount}</span>}
          </button>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-700"><X size={16} /></button>}
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100" data-testid="filters-panel">
            <select value={filterOrgType} onChange={function(e) { setFilterOrgType(e.target.value); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">{orgOpts}</select>
            <select value={filterPriority} onChange={function(e) { setFilterPriority(e.target.value); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">{prioOpts}</select>
            <select value={filterStatus} onChange={function(e) { setFilterStatus(e.target.value); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">{statOpts}</select>
            <select value={filterService} onChange={function(e) { setFilterService(e.target.value); }} className="px-3 py-2 border border-slate-200 rounded-lg text-sm">{svcOpts}</select>
          </div>
        )}
      </div>

      {/* Dashboard View */}
      {viewMode === 'dashboard' && stats && <DashboardView stats={stats} />}

      {/* Card View */}
      {viewMode === 'card' && <CardView clients={filteredClients} basePath={basePath} onEdit={handleEdit} onDelete={function(c) { setDeleteTarget(c); setDeleteError(null); setAdminPwd(''); }} isAdmin={isAdmin} />}

      {/* List View */}
      {viewMode === 'list' && <ListView clients={filteredClients} basePath={basePath} onEdit={handleEdit} onDelete={function(c) { setDeleteTarget(c); setDeleteError(null); setAdminPwd(''); }} isAdmin={isAdmin} sortField={sortField} sortDir={sortDir} onSort={handleSort} formatCurrency={formatCurrency} />}

      {filteredClients.length === 0 && viewMode !== 'dashboard' && (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200"><Building2 size={48} className="mx-auto text-slate-300 mb-4" /><p className="text-slate-600">No {isProspects ? 'prospects' : 'clients'} found.</p></div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && <DeleteDialog target={deleteTarget} isAdmin={isAdmin} adminPwd={adminPwd} setAdminPwd={setAdminPwd} error={deleteError} deleting={deleting} onConfirm={handleDelete} onCancel={function() { setDeleteTarget(null); setAdminPwd(''); setDeleteError(null); }} />}

      {showCreateModal && <ClientModal isOpen={showCreateModal} onClose={handleModalClose} client={selectedClient} defaultCategory={category || 'client'} onSuccess={function() { fetchClients(); fetchStats(); handleModalClose(); }} />}
      {showAIDiscover && <AIDiscoverOrgsModal isOpen={showAIDiscover} onClose={function() { setShowAIDiscover(false); }} onOrganizationsSaved={function() { fetchClients(); fetchStats(); }} />}
    </div>
  );
};

/* Card View */
function CardView({ clients, basePath, onEdit, onDelete, isAdmin }) {
  var navigate = require('react-router-dom').useNavigate();
  var cards = [];
  for (var i = 0; i < clients.length; i++) {
    cards.push(<ClientCard key={clients[i]._id} client={clients[i]} basePath={basePath} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} navigate={navigate} />);
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">{cards}</div>;
}

function ClientCard({ client, basePath, onEdit, onDelete, isAdmin, navigate }) {
  var ini = client.client_name ? client.client_name.charAt(0).toUpperCase() : '?';
  var fmtC = function(v) { return 'RM ' + (v / 1000).toFixed(0) + 'K'; };
  var svcItems = [];
  var svcs = client.relevant_services || [];
  var maxSvc = svcs.length > 3 ? 3 : svcs.length;
  for (var s = 0; s < maxSvc; s++) svcItems.push(<span key={svcs[s]} className="text-xs px-2 py-0.5 bg-primary/5 text-primary rounded border border-primary/10">{svcs[s]}</span>);
  if (svcs.length > 3) svcItems.push(<span key="more" className="text-xs text-slate-400">+{svcs.length - 3}</span>);
  var canDelete = !client.total_deals || client.total_deals === 0 || isAdmin;

  return (
    <div onClick={function() { navigate(basePath + '/' + client._id); }} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-all cursor-pointer group" data-testid={'client-card-' + client._id}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">{ini}</div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{client.client_name}</h3>
            <p className="text-xs text-slate-500">{client.organization_type || client.industry || 'N/A'}{client.sector ? ' \u00B7 ' + client.sector : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={function(e) { e.stopPropagation(); onEdit(client); }} className="text-sm text-primary hover:text-primary/80 px-2">Edit</button>
          {canDelete && <button onClick={function(e) { e.stopPropagation(); onDelete({ _id: client._id, client_name: client.client_name, has_deals: client.total_deals > 0 }); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {client.priority && <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (prioColors[client.priority] || 'bg-slate-100 text-slate-600')}>{client.priority}</span>}
        {client.prospecting_status && <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (statusColors[client.prospecting_status] || 'bg-slate-100 text-slate-600')}>{client.prospecting_status}</span>}
        {client.category && <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (client.category === 'client' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700')}>{client.category}</span>}
      </div>
      {svcItems.length > 0 && <div className="flex flex-wrap gap-1 mb-3">{svcItems}</div>}
      <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
        <div><p className="text-slate-500">Deals</p><p className="font-semibold text-slate-900">{client.total_deals || 0}</p></div>
        <div className="text-right"><p className="text-slate-500">Won Value</p><p className="font-semibold text-green-600">{fmtC(client.total_won_value || 0)}</p></div>
      </div>
    </div>
  );
}

/* List View with Sortable Columns */
function ListView({ clients, basePath, onEdit, onDelete, isAdmin, sortField, sortDir, onSort, formatCurrency }) {
  var navigate = require('react-router-dom').useNavigate();
  var cols = [
    { key: 'client_name', label: 'Name' },
    { key: 'organization_type', label: 'Type' },
    { key: 'industry', label: 'Industry' },
    { key: 'priority', label: 'Priority' },
    { key: 'prospecting_status', label: 'Status' },
    { key: 'total_deals', label: 'Deals' },
    { key: 'total_won_value', label: 'Won Value' },
  ];
  var headers = [];
  for (var ci = 0; ci < cols.length; ci++) {
    var col = cols[ci];
    var isActive = sortField === col.key;
    headers.push(
      <SortHeader key={col.key} label={col.label} isActive={isActive} dir={isActive ? sortDir : null} onClick={function(k) { return function() { onSort(k); }; }(col.key)} />
    );
  }
  headers.push(<th key="actions" className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>);

  var rows = [];
  for (var ri = 0; ri < clients.length; ri++) {
    var c = clients[ri];
    rows.push(<ListRow key={c._id} client={c} basePath={basePath} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} formatCurrency={formatCurrency} navigate={navigate} />);
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="clients-list-table">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>{headers}</tr></thead>
          <tbody className="divide-y divide-slate-100">{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

function SortHeader({ label, isActive, dir, onClick }) {
  return (
    <th onClick={onClick} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:bg-slate-100 select-none">
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (dir === 'asc' ? <ChevronUp size={14} className="text-primary" /> : <ChevronDown size={14} className="text-primary" />) : <ArrowUpDown size={14} className="text-slate-300" />}
      </div>
    </th>
  );
}

function ListRow({ client, basePath, onEdit, onDelete, isAdmin, formatCurrency, navigate }) {
  var canDelete = !client.total_deals || client.total_deals === 0 || isAdmin;
  return (
    <tr onClick={function() { navigate(basePath + '/' + client._id); }} className="hover:bg-slate-50 cursor-pointer" data-testid={'list-row-' + client._id}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">{client.client_name ? client.client_name.charAt(0).toUpperCase() : '?'}</div>
          <span className="font-medium text-slate-900">{client.client_name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{client.organization_type || '-'}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{client.industry || '-'}</td>
      <td className="px-4 py-3">{client.priority ? <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (prioColors[client.priority] || '')}>{client.priority}</span> : '-'}</td>
      <td className="px-4 py-3">{client.prospecting_status ? <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (statusColors[client.prospecting_status] || '')}>{client.prospecting_status}</span> : '-'}</td>
      <td className="px-4 py-3 text-sm text-slate-900 font-medium">{client.total_deals || 0}</td>
      <td className="px-4 py-3 text-sm text-green-600 font-medium">{formatCurrency(client.total_won_value || 0)}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={function(e) { e.stopPropagation(); onEdit(client); }} className="text-sm text-primary hover:text-primary/80 px-2">Edit</button>
          {canDelete && <button onClick={function(e) { e.stopPropagation(); onDelete({ _id: client._id, client_name: client.client_name, has_deals: client.total_deals > 0 }); }} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>}
        </div>
      </td>
    </tr>
  );
}

/* Delete Dialog */
function DeleteDialog({ target, isAdmin, adminPwd, setAdminPwd, error, deleting, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="delete-dialog">
      <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={function(e) { e.stopPropagation(); }}>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete {target.client_name}?</h3>
        {target.has_deals ? (
          <div>
            <p className="text-sm text-red-600 mb-3">This client has associated deals. Admin password required to delete.</p>
            <input type="password" value={adminPwd} onChange={function(e) { setAdminPwd(e.target.value); }} placeholder="Enter admin password" className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-3" data-testid="admin-password-input" />
          </div>
        ) : (
          <p className="text-sm text-slate-600 mb-4">This action cannot be undone. All associated contacts will also be deleted.</p>
        )}
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={onConfirm} disabled={deleting || (target.has_deals && !adminPwd)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid="confirm-delete-btn">{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

/* Dashboard View */
function DashboardView({ stats }) {
  return (
    <div className="space-y-4" data-testid="prospecting-dashboard">
      <div className="grid grid-cols-5 gap-4">
        <StatCard icon={Building2} label="Organizations" value={stats.total_organizations} color="text-blue-600" bg="bg-blue-50" />
        <StatCard icon={Users} label="Total Contacts" value={stats.total_contacts} color="text-green-600" bg="bg-green-50" />
        <StatCard icon={Mail} label="Emails Found" value={stats.emails_found} color="text-purple-600" bg="bg-purple-50" />
        <StatCard icon={AlertTriangle} label="High Priority" value={stats.high_priority} color="text-red-600" bg="bg-red-50" />
        <StatCard icon={Building2} label="Coverage Gaps" value={stats.coverage_gaps} color="text-amber-600" bg="bg-amber-50" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <BreakdownChart title="Pipeline Status" data={stats.pipeline_breakdown} />
        <BreakdownChart title="Organization Type" data={stats.type_breakdown} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <BreakdownChart title="Service Lines" data={stats.service_breakdown} />
        <BreakdownChart title="Sectors" data={stats.sector_breakdown} />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={'w-10 h-10 rounded-lg ' + bg + ' flex items-center justify-center'}><Icon size={20} className={color} /></div>
        <div><p className="text-2xl font-bold text-slate-900">{value || 0}</p><p className="text-xs text-slate-500">{label}</p></div>
      </div>
    </div>
  );
}

function BreakdownChart({ title, data }) {
  if (!data || Object.keys(data).length === 0) return <div className="bg-white rounded-lg border border-slate-200 p-5"><h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3><p className="text-sm text-slate-400">No data</p></div>;
  var entries = Object.entries(data);
  entries.sort(function(a, b) { return b[1] - a[1]; });
  var total = 0;
  for (var i = 0; i < entries.length; i++) total += entries[i][1];
  var barColors = ['bg-primary', 'bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-purple-500', 'bg-red-500', 'bg-indigo-500', 'bg-teal-500'];
  var bars = [];
  for (var bi = 0; bi < entries.length; bi++) {
    var pct = total > 0 ? Math.round(entries[bi][1] / total * 100) : 0;
    var w = total > 0 ? (entries[bi][1] / total * 100) : 0;
    bars.push(
      <div key={bi}>
        <div className="flex items-center justify-between text-xs mb-1"><span className="text-slate-600 truncate mr-2">{entries[bi][0]}</span><span className="text-slate-900 font-medium whitespace-nowrap">{entries[bi][1]} ({pct}%)</span></div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={'h-full rounded-full ' + barColors[bi % barColors.length]} style={{ width: w + '%' }}></div></div>
      </div>
    );
  }
  return <div className="bg-white rounded-lg border border-slate-200 p-5"><h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3><div className="space-y-2.5">{bars}</div></div>;
}

export default Clients;
